import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import styled from '@emotion/styled';
import { Grid, Select, selectClasses, type SelectOption } from '@kc-console/common-ui';
import { IcLocationRegular } from '@kep/kcds-icons';

import { useRegionEndpoint } from '@/shared/hooks';
import { navigateWithFullPageReload } from '@/shared/lib/routeProgress';

export default function GnbRegionSelect() {
  const router = useRouter();
  const { currentRegion = '', regionList } = useRegionEndpoint();

  const options: SelectOption<string>[] = useMemo(() => {
    return [
      {
        title: '리전',
        type: 'section',
        items: regionList?.map(region => {
          return {
            text: region || '',
            value: region || '',
          };
        }),
      },
    ];
  }, [regionList]);

  const tooltipMessage = useMemo(() => {
    return currentRegion ? `${currentRegion} 리전을 사용 중 입니다.` : '';
  }, [currentRegion]);

  const placeholder = useMemo(() => {
    return !currentRegion ? '리전을 선택해주세요.' : '';
  }, [currentRegion]);

  const handleRegionChange = useCallback(
    (region: unknown) => {
      const { region: regionQuery } = router.query;
      const currentRegionQuery = Array.isArray(regionQuery) ? regionQuery[0] : regionQuery;
      const nextRegion = String(region);
      if (nextRegion === currentRegionQuery) {
        return;
      }

      const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
      const firstPath = firstSegment ? `/${firstSegment}` : '/';
      navigateWithFullPageReload(`${firstPath}?region=${nextRegion}`);
    },
    [router.query],
  );

  return (
    <StyledGnbRegionSelect
      block
      options={options}
      value={currentRegion}
      renderValue={() => <GnbSelectDisplayArea>{currentRegion}</GnbSelectDisplayArea>}
      renderTooltip={() => tooltipMessage}
      placeholder={placeholder}
      onChange={handleRegionChange}
      startAdornment={<IcLocationRegular />}
    />
  );
}

const StyledGnbRegionSelect = styled(Select)`
  width: 100%;
  max-width: 200px;

  height: 48px;
  padding: 14px 12px;

  font-size: 13px !important;
  font-weight: 400;
  text-align: left;

  border-radius: 0;
  background-color: inherit;
  color: rgba(220, 225, 232, 1);

  svg {
    fill: rgba(220, 225, 232, 1);
  }

  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  &:hover {
    background-color: ${({ theme }) =>
      theme.color['surface/global/transparent/strong/hover']} !important;
  }
  &:active {
    background-color: ${({ theme }) =>
      theme.color['surface/global/transparent/strong/press']} !important;
  }
  &.Mui-disabled {
    background-color: transparent;
    span {
      -webkit-text-fill-color: ${({ theme }) => theme.color['text/global/disabled']};
    }
    svg {
      fill: ${({ theme }) => theme.color['text/global/disabled']};
    }
  }
  &.Mui-focused {
    // Note : Select 에서 focusedVisible 처리가 가능한 별도의 방안 고려
    background-color: transparent;
  }
`;

const GnbSelectDisplayArea = styled(Grid)`
  min-width: 120px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  .${selectClasses.root}.Mui-disabled & {
    color: ${({ theme }) => theme.color['text/global/disabled']};
    -webkit-text-fill-color: ${({ theme }) => theme.color['text/global/disabled']};
  }
`;
